import { ChakraProvider } from "@chakra-ui/react";
import { useEffect } from "react";
import axios from "axios";
import CategoriesScreen from "./screens/CategoriesScreen";
import JobsScreen from "./screens/JobsScreen";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import Header from "./components/Header";
import LandingScreen from "./screens/LandingScreen";
import JobScreen from "./screens/JobScreen";
import JobEditorScreen from "./screens/JobEditor";
import TotalsScreen from "./screens/TotalsScreen";
import LoginScreen from "./screens/LoginScreen";
import EmailVerificationScreen from "./screens/EmailVerificationScreen";
import PasswordResetScreen from "./screens/PasswordResetScreen";
import RegistrationScreen from "./screens/RegistrationScreen";
import JobSearchScreen from "./screens/JobSearchScreen";
import SendSMSScreen from "./screens/SendSMSScreen";
import BulkSendSMSScreen from "./screens/BulkSendSMSScreen";
import MessageScreen from "./screens/MessageScreen";
import TestingScreen from "./screens/TestingScreen";
import SetupCheckLists from "./screens/setup/SetupCheckLists";
import ChecklistScreen from "./screens/ChecklistScreen";
import Setup from "./screens/Setup";
import DashboardScreen from "./screens/DashboardScreen";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import LogViewerScreen from "./screens/LogViewerScreen";
import RevenueStatsScreen from "./screens/RevenueStatsScreen";
import PostcodeStatsScreen from "./screens/PostcodeStatsScreen";
import CallStatisticsScreen from "./screens/CallStatisticsScreen";
import CallerIdHistoryScreen from "./screens/CallerIdHistoryScreen";
import TasksScreen from "./screens/TasksScreen";
import OrdersScreen from "./screens/OrdersScreen";
import JobLogSearchScreen from "./screens/JobLogSearchScreen";
import CallerIdLiveListener from "./components/CallerIdLiveListener";
import { setBusinessTimeZone } from "./global-client";

function App() {
  useEffect(() => {
    const loadBusinessTimeZone = async () => {
      try {
        const { data } = await axios.get("/api/setup")
        if (data?.businessTimeZone) {
          setBusinessTimeZone(data.businessTimeZone)
        }
      } catch (error) {
        // Keep local/default timezone when setup API is unavailable.
      }
    }

    loadBusinessTimeZone()
  }, [])

  return (
    <ChakraProvider>
      <Router>
        <CallerIdLiveListener />
        <Header />
        <main>
          <Routes>
            <Route path='/' element={<LandingScreen />} />
            <Route path='/login' element={<LoginScreen />} />
            <Route path='/registration' element={<RegistrationScreen />} />
            <Route path='/verify-email/:token' element={<EmailVerificationScreen />} />
            <Route path='/password-reset/:token' element={<PasswordResetScreen />} />

            {/* Protected Routes */}
            <Route
              path='/dashboard'
              element={
                <ProtectedRoute>
                  <DashboardScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/jobs'
              element={
                <ProtectedRoute>
                  <JobsScreen bookings={false} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/categories'
              element={
                <ProtectedRoute>
                  <CategoriesScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/bookings'
              element={
                <ProtectedRoute>
                  <JobsScreen bookings={true} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/job/:num/'
              element={
                <ProtectedRoute>
                  <JobScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/jobs/new'
              element={
                <ProtectedRoute>
                  <JobEditorScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/jobs/edit/:jobNo'
              element={
                <ProtectedRoute>
                  <JobEditorScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/totals'
              element={
                <ProtectedRoute>
                  <TotalsScreen bookings={false} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/job-search'
              element={
                <ProtectedRoute>
                  <JobSearchScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/send-sms'
              element={
                <ProtectedRoute>
                  <SendSMSScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/send-sms/bulk'
              element={
                <ProtectedRoute>
                  <BulkSendSMSScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/messages'
              element={
                <ProtectedRoute>
                  <MessageScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/testing'
              element={
                <ProtectedRoute>
                  <TestingScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/checklists'
              element={
                <ProtectedRoute>
                  <SetupCheckLists />
                </ProtectedRoute>
              }
            />
            <Route
              path='/checklist'
              element={
                <ProtectedRoute>
                  <ChecklistScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/setup'
              element={
                <ProtectedRoute>
                  <Setup />
                </ProtectedRoute>
              }
            />
            <Route
              path='/logs'
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <LogViewerScreen />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path='/revenue-stats'
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <RevenueStatsScreen />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path='/postcode-stats'
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <PostcodeStatsScreen />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path='/call-statistics'
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <CallStatisticsScreen />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path='/caller-id-history'
              element={
                <ProtectedRoute>
                  <CallerIdHistoryScreen />
                </ProtectedRoute>
              }
            />

            <Route
              path='/job-log-search'
              element={
                <ProtectedRoute>
                  <JobLogSearchScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tasks'
              element={
                <ProtectedRoute>
                  <TasksScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path='/orders'
              element={
                <ProtectedRoute>
                  <OrdersScreen />
                </ProtectedRoute>
              }
            />
          </Routes>
        </main>
      </Router>
    </ChakraProvider>
  );
}

export default App;
